<template>
    <div class="container-fluid px-2 px-md-3 pt-3">
        <span class="small" role="button" @click="goBack()">
            <i class="fa-solid fa-chevron-left me-2"></i>{{ $t('common.back') }}
        </span>

        <h3 class="my-3">{{ $t('maintenanceLog.title') }}</h3>

        <div v-if="loading" class="text-center text-body-secondary my-5">
            <div class="spinner-border spinner-border-sm mb-2" aria-hidden="true"></div>
            <div role="status">
                <div>{{ $t('common.loading') }}</div>
                <div v-if="showSlowHint" class="small mt-2">{{ $t('maintenanceLog.slowHint') }}</div>
            </div>
        </div>
        <!-- A failed request is not the same as a missing record: show the error
             with a retry instead of the misleading "not found" page. -->
        <div v-else-if="loadError">
            <div class="alert alert-danger">{{ $t('maintenanceLog.loadFailed') }}</div>
            <button class="btn btn-primary" @click="fetchLog()">{{ $t('common.retry') }}</button>
        </div>
        <div v-else>
            <div v-if="log">
                <div class="text-body-secondary small mb-3">
                    <span v-if="log.project_name">{{ log.project_name }}</span>
                    <span v-if="log.object_title"> &middot; {{ log.object_title }}</span>
                    <span v-if="log.from_date"> &middot; {{ log.from_date }} &ndash; {{ log.to_date }}</span>
                </div>
                <MaintenanceLog :log="log" />
            </div>
            <NotFound v-else :item="$t('maintenanceLog.title')" />
        </div>
    </div>
</template>

<script>
import axios from '@/services/axios';
import { setDocumentTitle } from '@/composables/usePageTitle';
import NotFound from '@/components/NotFound.vue';
import MaintenanceLog from '@/components/MaintenanceLog.vue';

export default {
    components: {
        NotFound,
        MaintenanceLog
    },
    data() {
        return {
            log: null,
            loading: true,
            loadError: false,
            showSlowHint: false,
            slowHintTimer: null
        };
    },
    computed: {
        // Entity ('realestates' | 'projects' | 'objects') and id param name come
        // from the route meta so this one view serves all three.
        entity() {
            return this.$route.meta.entity;
        },
        entityId() {
            return this.$route.params[this.$route.meta.idParam];
        }
    },
    methods: {
        async fetchLog() {
            this.loading = true;
            this.loadError = false;
            this.showSlowHint = false;
            // Past a few seconds a bare spinner starts to read as a hang, so
            // explain that the wait is expected rather than broken.
            this.clearSlowHintTimer();
            this.slowHintTimer = setTimeout(() => {
                this.showSlowHint = true;
            }, 5000);

            try {
                const response = await axios.get(`/${this.entity}/${this.entityId}/maintenance_log`, {
                    // The report is aggregated server-side and axios has no global
                    // default, so without this a stalled request spins forever.
                    timeout: 60000
                });
                this.log = response.data.data;
                setDocumentTitle(this.log?.project_name || this.$t('maintenanceLog.title'));
            } catch (error) {
                console.error('Error fetching maintenance log:', error);
                this.log = null;
                // A 404 means the record really is missing, which NotFound already
                // states correctly. Everything else (timeout, 500, offline) is a
                // failure to load and gets the error state with a retry.
                this.loadError = error.response?.status !== 404;
            } finally {
                this.clearSlowHintTimer();
                this.loading = false;
            }
        },
        clearSlowHintTimer() {
            if (this.slowHintTimer) {
                clearTimeout(this.slowHintTimer);
                this.slowHintTimer = null;
            }
        },
        goBack() {
            if (this.$route.meta.internalNavigation && window.history.length > 2) {
                this.$router.go(-1);
            } else {
                this.$router.push('/');
            }
        }
    },
    mounted() {
        this.fetchLog();
    },
    unmounted() {
        this.clearSlowHintTimer();
    }
};
</script>
